import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft } from "lucide-react";
import Seo from "../components/Seo";
import BoatGallery from "../components/BoatGallery";
import BookingOutage from "../components/BookingOutage";
import BookingPanel from "../components/BookingPanel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { todayISO } from "../components/BookingWidget";
import { getBoat, getConfig, mediaUrl } from "../lib/api";
import { chargeUnitLabel, prettyDate } from "../lib/format";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

export default function BoatDetail() {
  const { assetId } = useParams();
  // The marina this boat belongs to comes from the URL, so one page serves both
  // locations and a boat is never priced or booked against the wrong location.
  const slug = useLocation().pathname.split("/")[2];
  const LOC = LOCATIONS[slug] || WB;
  const [params, setParams] = useSearchParams();
  const min = todayISO();
  const qs = params.get("date");
  const [date, setDate] = useState(qs && qs >= min ? qs : min);
  const [state, setState] = useState({ loading: true, boat: null, error: null });
  const reqRef = useRef(0);
  const [showDegraded, setShowDegraded] = useState(false);

  useEffect(() => {
    getConfig()
      .then((c) => setShowDegraded(Boolean(c.showDegradedBanner)))
      .catch(() => setShowDegraded(false));
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (next.get("date") !== date) {
      next.set("date", date);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    const token = ++reqRef.current;
    setState({ loading: true, boat: null, error: null });
    getBoat(assetId, LOC.locationId, date)
      .then((b) => {
        if (token === reqRef.current) setState({ loading: false, boat: b, error: null });
      })
      .catch((e) => {
        if (token === reqRef.current) {
          setState({ loading: false, boat: null, error: e.message || "Unable to load this boat" });
        }
      });
  }, [assetId, date, LOC.locationId]);

  const boat = state.boat;
  const listing = boat?.listing;
  const name = boat?.displayName || `Boat ${assetId}`;
  const type = boat?.assetTypeName;

  const jsonLd = useMemo(
    () =>
      boat && boat.rates?.tiers?.length
        ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name,
            ...(type ? { category: type } : {}),
            ...(listing?.heroImageUrl ? { image: mediaUrl(listing.heroImageUrl) } : {}),
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: Math.min(...boat.rates.tiers.map((t) => t.amount)),
              availability: boat.bookable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            },
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boat?.assetId, boat?.date, boat?.bookable, name, type, listing?.heroImageUrl]
  );

  return (
    <>
      <Seo
        title={`${name}${type ? ` — ${type}` : ""} Rental | West Branch Marina`}
        description={`Reserve the ${name}${type ? ` (${type})` : ""} at ${LOC.park}. Live rates, durations and departure times for your date.`}
        path={`/boat-rentals/${slug}/boats/${assetId}`}
        image={listing?.heroImageUrl ? mediaUrl(listing.heroImageUrl) : "/images/hero_pontoon_golden.jpeg"}
        jsonLd={jsonLd}
      />

      <div className="bg-navy pt-20 lg:pt-24">
        <div className="mx-auto max-w-7xl px-5 pb-6 lg:px-10">
          <Link
            to={`/boat-rentals/${slug}?date=${date}`}
            data-testid="boat-detail-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-ember transition-colors"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            All boats at {LOC.name.replace(" Boat Rentals", "")}
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-10 lg:py-16">
        {state.loading && (
          <div data-testid="boat-detail-loading" className="grid gap-10 lg:grid-cols-[1.5fr_1fr]">
            <div className="animate-pulse rounded-3xl bg-sand-card" style={{ aspectRatio: "3 / 2" }} />
            <div className="h-96 animate-pulse rounded-3xl bg-sand-card" />
          </div>
        )}

        {!state.loading && state.error && (
          <BookingOutage testId="boat-detail-outage" />
        )}

        {boat && (
          <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-14">
            <div>
              <p className="eyebrow text-lake">{type || "Watercraft"}</p>
              <h1
                data-testid="boat-detail-name"
                className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-navy"
              >
                {name}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {boat.serviceName} · {prettyDate(date)}
                {typeof boat.capacity === "number" && (
                  <span data-testid="boat-detail-capacity"> · Up to {boat.capacity} passengers</span>
                )}
              </p>

              {showDegraded && boat.degraded?.length > 0 && (
                <div
                  data-testid="boat-detail-degraded-banner"
                  role="status"
                  className="mt-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                  <p className="text-sm text-amber-900">
                    <strong className="font-bold">Internal notice:</strong> unavailable data ({boat.degraded.join(", ")}
                    ). Affected sections are hidden.
                  </p>
                </div>
              )}

              <div className="mt-8">
                <BoatGallery images={listing?.images || []} displayName={name} assetTypeName={type} />
              </div>

              {listing?.description && (
                <p data-testid="boat-detail-description" className="mt-8 text-base leading-relaxed text-slate-700">
                  {listing.description}
                </p>
              )}

              {listing?.highlights?.length > 0 && (
                <section data-testid="boat-detail-highlights" className="mt-12">
                  <h2 className="font-display text-xl font-bold text-navy">This boat at a glance</h2>
                  <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {listing.highlights.map((h) => (
                      <div
                        key={h.key}
                        data-testid={`highlight-${h.key.replace(/\s+/g, "-")}`}
                        className="flex justify-between gap-4 border-b border-sand-border pb-2 text-sm"
                      >
                        <dt className="text-slate-600">{h.label}</dt>
                        <dd className="text-right font-semibold text-navy">{h.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              {listing?.sections?.length > 0 && (
                <section data-testid="boat-detail-specs" className="mt-12">
                  {listing.sections.map((sec, si) => (
                    <div key={`${sec.label}-${si}`} className={si > 0 ? "mt-10" : ""}>
                      <h2 className="font-display text-xl font-bold text-navy">{sec.label}</h2>
                      <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                        {sec.items.map((it) => (
                          <div
                            key={it.dimensionId ?? it.name}
                            data-testid={`spec-item-${it.dimensionId ?? it.name}`}
                            className="flex justify-between gap-4 border-b border-sand-border pb-2 text-sm"
                          >
                            <dt className="text-slate-600">{it.name}</dt>
                            <dd className="font-semibold text-navy">{String(it.value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </section>
              )}

              {listing?.policies?.length > 0 && (
                <section data-testid="boat-detail-policies" className="mt-12">
                  <h2 className="font-display text-xl font-bold text-navy">Policies</h2>
                  <Accordion
                    type="single"
                    collapsible
                    className="mt-4 divide-y divide-sand-border overflow-hidden rounded-2xl border border-sand-border bg-white"
                  >
                    {listing.policies.map((p) => (
                      <AccordionItem key={p.policyMapId} value={String(p.policyMapId)} className="border-0 px-5">
                        <AccordionTrigger
                          data-testid={`policy-trigger-${p.policyMapId}`}
                          className="py-4 text-left font-display text-base font-semibold text-navy hover:no-underline"
                        >
                          <span className="flex flex-wrap items-center gap-2">
                            {p.heading}
                            {p.requiresAck && (
                              <span className="rounded-full bg-ember/10 px-2 py-0.5 text-[11px] font-bold text-ember">
                                Acceptance required
                              </span>
                            )}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent
                          data-testid={`policy-content-${p.policyMapId}`}
                          className="pb-5 text-sm leading-relaxed text-slate-700"
                        >
                          {p.hasCharge && typeof p.chargeAmount === "number" && (
                            <p
                              data-testid={`policy-charge-${p.policyMapId}`}
                              className="mb-3 inline-block rounded-lg bg-sand-card px-3 py-1.5 font-bold text-navy"
                            >
                              Charge: {chargeUnitLabel(p.chargeAmount, p.chargeUnitName)}
                            </p>
                          )}
                          <p className="whitespace-pre-line">{p.description}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>
              )}
            </div>

            <div className="lg:sticky lg:top-28 lg:self-start">
              <BookingPanel
                boat={boat}
                date={date}
                minDate={min}
                onDateChange={setDate}
                showDegraded={showDegraded}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
